import { useMutation } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import type { AttendanceBoardRow } from '../api/types';
import { useApi } from '../auth/ApiProvider';
import { parseDeciCelsius } from '../format';
import { useMemberSwitch } from '../hooks/useTenantSettings';
import { useI18n } from '../i18n/I18nProvider';
import { Field, Input, Textarea } from './Field';
import { Button, Card, Row, Stack } from './primitives';
import { ErrorState } from './states';

/** What was typed at the door: a temperature in degrees and anything noticed, both optional (FR-ATT-03). */
export interface ArrivalReading {
  temperature: string;
  note: string;
}

export const NO_ARRIVAL_READING: ArrivalReading = { temperature: '', note: '' };

/** Whether the nursery takes a health check at arrival, as `/tenants/current` serves it to whoever checks in (NR-1). */
export function useArrivalHealthCheck(): boolean {
  return useMemberSwitch('attendance.health_check');
}

/** The check-in's reading fields, or null where the temperature typed is not a number. */
export function arrivalBody(reading: ArrivalReading): { temperatureDeciCelsius: number | null; symptomsNote: string | null } | null {
  const typed = reading.temperature.trim();
  const temperatureDeciCelsius = typed ? parseDeciCelsius(typed) : null;
  if (typed && temperatureDeciCelsius === null) return null;
  return { temperatureDeciCelsius, symptomsNote: reading.note.trim() || null };
}

/** The temperature and the note, for a check-in form that has the check switched on. */
export function ArrivalCheckFields({ value, onChange, showInvalid }: {
  value: ArrivalReading;
  onChange: (next: ArrivalReading) => void;
  /** Set once a submit found the temperature unreadable; the field says so until it is changed. */
  showInvalid: boolean;
}) {
  const { t } = useI18n();

  return (
    <>
      <Field
        label={t('arrival.temperature')}
        hint={t('arrival.temperature.hint')}
        error={showInvalid ? t('arrival.temperature.invalid') : null}
      >
        {(props) => (
          <Input
            {...props}
            inputMode="decimal"
            dir="ltr"
            value={value.temperature}
            onChange={(event) => onChange({ ...value, temperature: event.target.value })}
          />
        )}
      </Field>
      <Field label={t('arrival.note')} hint={t('arrival.note.hint')}>
        {(props) => (
          <Textarea
            {...props}
            rows={2}
            value={value.note}
            onChange={(event) => onChange({ ...value, note: event.target.value })}
          />
        )}
      </Field>
    </>
  );
}

/** A reading already taken, as a board row shows it: "37.8°", the note after it. */
export function ArrivalReadingText({ row }: { row: Pick<AttendanceBoardRow, 'temperatureDeciCelsius' | 'symptomsNote'> }) {
  const { t, formatNumber } = useI18n();
  if (row.temperatureDeciCelsius === null && !row.symptomsNote) return null;

  return (
    <span className="dg-muted dg-text-sm">
      {row.temperatureDeciCelsius !== null && t('arrival.reading', { value: formatNumber(row.temperatureDeciCelsius / 10) })}
      {row.temperatureDeciCelsius !== null && row.symptomsNote && ' · '}
      {row.symptomsNote}
    </span>
  );
}

/**
 * Checking a child in where the nursery takes the check: the reading first, then the arrival, so the guardians' "arrived
 * safely" goes out once with the reading kept beside it. Both fields may stay empty; the check is offered, never forced.
 */
export function CheckInDialog({ row, onClose, onDone }: {
  row: AttendanceBoardRow; onClose: () => void; onDone: () => void;
}) {
  const api = useApi();
  const { t } = useI18n();
  const [reading, setReading] = useState<ArrivalReading>(NO_ARRIVAL_READING);
  const [unreadable, setUnreadable] = useState(false);

  const checkIn = useMutation({
    mutationFn: (body: NonNullable<ReturnType<typeof arrivalBody>>) => api.attendance.checkIn({ childId: row.childId, ...body }),
    onSuccess: onDone,
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const body = arrivalBody(reading);
    setUnreadable(body === null);
    if (body) checkIn.mutate(body);
  };

  return (
    <Card title={t('arrival.title')} hint={row.preferredName ?? row.firstName}>
      <form noValidate onSubmit={submit}>
        <Stack>
          {checkIn.isError && <ErrorState error={checkIn.error} />}
          <ArrivalCheckFields
            value={reading}
            // A changed figure takes the "unreadable" message away; the next submit reads it again.
            onChange={(next) => { setReading(next); setUnreadable(false); }}
            showInvalid={unreadable}
          />
          <Row>
            <Button type="submit" busy={checkIn.isPending} busyLabel={t('action.saving')}>
              {t('attendance.checkIn')}
            </Button>
            <Button variant="ghost" onClick={onClose}>{t('action.cancel')}</Button>
          </Row>
        </Stack>
      </form>
    </Card>
  );
}
